import {booleanAttribute, ChangeDetectionStrategy, Component, computed, Injectable, input, output} from "@angular/core";
import {initDebugger} from "felles-ng2/støttefunksjoner/debug";
import {DokumentId, DokumentRessursClient, DokumentsigneringRessursClient, DokumentVersjonId, DokumentVersjonModell, PotensiellDokumentSignatarModell, ProtokollførtDeltakerstatus, ProtokollførtMøtedeltakerModell, SigneringStatus} from "felles-ng2/tjenester/ressurser/generertNg2HttpKlient";
import {TranslateModule} from "@ngx-translate/core";
import {MøteModellDLM} from "felles-ng2/tjenester/datalager/MøteDatatjeneste";
import {MatButton} from "@angular/material/button";
import {dialogRespons} from "felles-ng2/infrastruktur/material/matDialogHjelpere";
import {SigneringSignerDialogKomponent} from "web-ng2/moduler/signering/modale/signering-signer-dialog/signering-signer-dialog";
import {i18n} from "felles-ng2/tjenester/i18n/Oversettelser";
import {MatDialog} from "@angular/material/dialog";
import {BrukerkontekstTilbyder} from "felles-ng2/tjenester/BrukerkontekstTilbyder";
import {DokumentSigneringInformasjon} from "felles-ng2/tjenester/DokumentSigneringInformasjon";
import {MatIcon} from "@angular/material/icon";

const debug = initDebugger('MøteprotokollSignatarKomponent');

/**
 * Viser signatar, brukt ved utlisting av signatarer.
 * Dersom {@link #medSigneringsstoette() == true} vises knapp for signering eller informasjon om eksisterende signatur.
 */
@Component({
    selector: 'moeteprotokoll-signatar',
    host: {'data-testid': 'signaturfelt'},
    templateUrl: 'møteprotokoll-signatar.html',
    styleUrl: 'møteprotokoll-signatar.scss',
    imports: [
        TranslateModule,
        MatButton,
        MatIcon
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class MøteprotokollSignatarKomponent {

    /** Leverer event når bruker har signert */
    vedSignert = output<void>();

    signatar = input.required<ProtokollførtMøtedeltakerModell>();
    /** Dersom sann eksponeres funksjonalitet for å signere (dersom bruker skal signere protokollen) eller vise eksisterende signatur */
    medSigneringsstoette = input(false, {transform: booleanAttribute});
    /** Må angis dersom {@link #medSingeringsstoette() == true} */
    protokollDokumentversjon = input<DokumentVersjonModell | null>();
    protokollDokumentId = input<DokumentId | null>();
    /** Må angis dersom {@link #medSingeringsstoette() == true} */
    moete = input<MøteModellDLM | null>();

    viserSigneringsknapp = computed(() => this.beregnViserSigneringsknapp());
    signeringsinformasjon = computed(() => this.signatar().harSignert ?
      this.dokumentSigneringInformasjon.signaturinformasjonSomTekst(this.signatar().signaturinformasjon!, '<br>') : '');


    statuser = ProtokollførtDeltakerstatus;


    constructor(private avhengigheter: MøteprotokollSignatarKomponentAvhengigheter,
                private dialog: MatDialog,
                private brukerkontekst: BrukerkontekstTilbyder,
                private dokumentSigneringInformasjon: DokumentSigneringInformasjon) {
    }

    async signer() {
        const signert = await dialogRespons(SigneringSignerDialogKomponent.vis(
          this.dialog,
          this.avhengigheter.hentProtokollDokument(this.protokollDokumentId()!),
          this.avhengigheter.hentSignatarinformasjonForAktivBruker(this.protokollDokumentId()!)
        ));

        if (signert) this.vedSignert.emit();
    }

    //private hjelpemetoder

    private beregnViserSigneringsknapp(): boolean {
        return this.medSigneringsstoette() &&
          !this.signatar().harSignert &&
          this.signatar().medlem.brukerId === this.brukerkontekst.autentisertBruker().bruker.id &&
          [SigneringStatus.tilSignering, SigneringStatus.delvisSignert].includes(this.moete()!.protokollSigneringStatus);
    }
}

@Injectable()
export class MøteprotokollSignatarKomponentAvhengigheter {
    constructor(private dokumentsigneringRessurs: DokumentsigneringRessursClient, private dokumentRessursClient: DokumentRessursClient) {
    }

    hentSignatarinformasjonForAktivBruker(dokumentId: DokumentId): Promise<PotensiellDokumentSignatarModell> {
        return this.dokumentsigneringRessurs.signatarinformasjonForAktivBruker(dokumentId).somPromiseVisStandardMeldingVedFeil(i18n('Kunne ikke laste signatarinformasjon'));
    }

    hentProtokollDokument(dokumentId: DokumentId): Promise<DokumentVersjonModell> {
        return this.dokumentRessursClient.hentGjeldendeDokumentversjon(dokumentId).somPromiseVisStandardMeldingVedFeil(i18n('Kunne ikke laste protokolldokument'));
    }
}
